import { TCommonError } from "@models/common";
import {
  TAssetListItem,
  TAssetListReq,
  TAssetLookupsResponse,
  TAssetSaveResult,
  TAssetViewResponse,
  TUpdateAssetDetailRequest,
} from "@models/asset";
import { axiosInstance } from "./axiosInstance";
import { TAxiosResponse } from "types";

const orUndefined = <T>(value: T) => value || undefined;
const orUndefinedIfEmpty = <T>(arr?: T[]) => (arr?.length ? arr : undefined);

const buildAssetListParams = (payload: TAssetListReq) => ({
  SessionCode: orUndefined(payload.sessionCode),
  InventoryYearMonth: orUndefined(payload.inventoryYearMonth),
  CompanyCode: orUndefined(payload.companyCode),
  StoreCode: orUndefined(payload.storeCode),
  StatusCodes: orUndefinedIfEmpty(payload.statusCodes),
  DepartmentCodes: orUndefinedIfEmpty(payload.departmentNames),
  AssetType: orUndefined(payload.assetType),
  Location: orUndefined(payload.location),
  HasPhoto: payload.hasPhoto,
  AssetNo: orUndefined(payload.assetNo),
  AcquisitionDateFrom: orUndefined(payload.acquisitionDateFrom),
  AcquisitionDateTo: orUndefined(payload.acquisitionDateTo),
  AcquisitionCostFrom: payload.acquisitionCostFrom,
  AcquisitionCostTo: payload.acquisitionCostTo,
  SortBy: orUndefined(payload.sortBy),
  Ascending: payload.ascending,
});

const assetApi = {
  /**
   * GET /api/v1/assets/{assetId} — Load full asset detail for PC005.
   * Contract: vams_docs/Tasks/PC005/api-integration.md §2.1
   * Status: CAN CALL REAL — 8/9 fields already in DTO; sealRequestQuantity absent until BE-1+BE-2.
   */
  getAssetById: async (assetId: string) => {
    const url = `/api/v1/assets/${assetId}`;
    const response: TAxiosResponse<TAssetViewResponse, TCommonError> =
      await axiosInstance.get(url);
    return response;
  },

  /**
   * GET /api/v1/assets — List assets for PC004.
   * Contract: vams_docs/Tasks/PC004/api-integration.md §2.1
   */
  getAssets: async (payload: TAssetListReq) => {
    const url = "/api/v1/assets";
    const response: TAxiosResponse<TAssetListItem[], TCommonError> =
      await axiosInstance.get(url, {
        params: {
          ...buildAssetListParams(payload),
          Page: payload.page,
          PageSize: payload.pageSize,
        },
      });
    return response;
  },

  /**
   * GET /api/v1/assets/export — Export CSV theo cùng bộ filter với getAssets (bỏ paging).
   * Contract: vams_docs/Tasks/PC004/analyzing-be.md §2.4 (G-EXPORT).
   */
  exportAssets: async (payload: TAssetListReq) => {
    const url = "/api/v1/assets/export";
    const response = await axiosInstance.get(url, {
      params: buildAssetListParams(payload),
      responseType: "blob",
    });
    return response;
  },

  /**
   * DELETE /api/v1/assets/{assetId} — Soft delete dòng 不記載 (status UNLISTED).
   * Contract: vams_docs/Tasks/PC004/analyzing-be.md §2.4 (G-DELETE).
   */
  deleteAsset: async (assetId: string) => {
    const url = `/api/v1/assets/${assetId}`;
    const response: TAxiosResponse<null, TCommonError> = await axiosInstance.delete(url);
    return response;
  },

  /**
   * GET /api/v1/assets/lookups — Dropdown options cho 種類 [4.3]/部門 [4.4]/所属 [4.6] (PC004) và
   * 部門 [15]/所属 [16] (PC005). Đã hoạt động thật ở BE — trả `departments`/`locations`/`assetTypes`,
   * scope theo store cho role không phải ADMIN. Xem vams_docs/Tasks/PC004/analyzing-be.md §2.2.
   */
  getAssetLookups: async () => {
    const url = "/api/v1/assets/lookups";
    const response: TAxiosResponse<TAssetLookupsResponse, TCommonError> =
      await axiosInstance.get(url);
    return response;
  },

  /**
   * PUT /api/v1/assets/{assetId} — Save editable fields + status toggle for PC005.
   * Contract: vams_docs/Tasks/PC005/api-integration.md §2.2
   */
  updateAssetDetail: async (assetId: string, payload: TUpdateAssetDetailRequest) => {
    const url = `/api/v1/assets/${assetId}`;
    const response: TAxiosResponse<TAssetSaveResult, TCommonError> =
      await axiosInstance.put(url, payload);
    return response;
  },
};

export { assetApi };
